import { useState, useMemo, useEffect } from 'react';
import { useAccount, useReadContract } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { parseUnits } from 'viem';
import { Button } from '../components/ui/Button';
import { Card, StatCard, Badge } from '../components/ui/Card';
import { Input, FormField } from '../components/ui/Input';
import { useBuyFlow, useTxReceipt } from '../hooks/useBuyFlow';
import { usePresaleStats } from '../hooks/usePresaleStats';
import {
  PRESALE_ABI,
  PRESALE_ADDRESS,
  AIML_TOKEN_ADDRESS,
  USDT_DECIMALS,
} from '../lib/contracts';
import { fmtAiml, fmtUsdt } from '../lib/format';
import { useToast } from '../contexts/ToastContext';

const BLOCKAID_BANNER_DISMISSED_KEY = 'altcoins.blockaidBannerDismissed';

export default function Buy() {
  const { address, isConnected } = useAccount();
  const [usdt, setUsdt] = useState('100');
  const presale = usePresaleStats();
  const flow = useBuyFlow();
  const receipt = useTxReceipt(flow.txHash ?? undefined);
  const toast = useToast();

  const referralCode =
    typeof window !== 'undefined' ? localStorage.getItem('altcoins.refCode') ?? '' : '';

  const usdtNum = parseFloat(usdt) || 0;

  // BlockAid warning banner — dismissible, remembered in localStorage
  const [showBlockaidBanner, setShowBlockaidBanner] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setShowBlockaidBanner(
      localStorage.getItem(BLOCKAID_BANNER_DISMISSED_KEY) !== 'true',
    );
  }, []);
  const dismissBlockaidBanner = () => {
    localStorage.setItem(BLOCKAID_BANNER_DISMISSED_KEY, 'true');
    setShowBlockaidBanner(false);
  };

  const usdtRaw = useMemo(() => {
    try {
      if (!usdt || usdtNum <= 0) return 0n;
      return parseUnits(usdt, USDT_DECIMALS);
    } catch {
      return 0n;
    }
  }, [usdt, usdtNum]);

  const quote = useReadContract({
    address: PRESALE_ADDRESS,
    abi: PRESALE_ABI,
    functionName: 'getTokenAmountUSDT',
    args: usdtRaw > 0n ? [usdtRaw] : undefined,
    query: { enabled: usdtRaw > 0n },
  });

  const tokensOut = useMemo(() => {
    if (quote.data === undefined) return 0;
    return Number(quote.data as bigint);
  }, [quote.data]);

  // Once we have an on-chain receipt confirming the buy, surface a toast and ensure
  // the flow shows the 'done' state even if the buy() promise hung mid-flight.
  useEffect(() => {
    if (receipt.data && flow.txHash && flow.step !== 'done' && flow.step !== 'error') {
      // The transaction confirmed on-chain. Nudge the UI forward.
      toast.success('Purchase confirmed on-chain!');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt.data, flow.txHash]);

  const canBuy = usdtNum > 0;
  const isWorking =
    flow.step === 'approving' ||
    flow.step === 'approve-confirming' ||
    flow.step === 'buying' ||
    flow.step === 'buy-confirming' ||
    flow.step === 'attributing' ||
    flow.step === 'switching-chain';

  async function handleBuy() {
    if (!isConnected || !address) {
      toast.error('Connect a wallet first');
      return;
    }
    if (presale.paused) {
      toast.error('Sales are paused right now. Please try later.');
      return;
    }
    await flow.buy(usdt, referralCode || undefined);
  }

  // -- Subcomponents -----------------------------------------------------

  // Shown above the form: explains the BlockAid "deceptive request" warning.
  const BlockaidNotice = () =>
    showBlockaidBanner ? (
      <div className="max-w-2xl mx-auto mb-6 p-4 rounded-2xl bg-violet-500/5 border border-violet-500/20 flex items-start gap-3">
        <span className="text-xl shrink-0">🛡️</span>
        <div className="flex-1 text-sm">
          <div className="font-display font-semibold text-ink mb-1">
            About wallet security warnings
          </div>
          <p className="text-ink-700 leading-relaxed">
            Your wallet may show a security warning when approving — this is automatic for
            recently deployed contracts and is <strong>not specific to us</strong>. Our
            contract is{' '}
            <a
              href="/AIML-Audit.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 underline"
            >
              audited
            </a>{' '}
            and{' '}
            <a
              href={`https://polygonscan.com/address/${PRESALE_ADDRESS}#code`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 underline"
            >
              verified on Polygonscan
            </a>
            . You can safely confirm.
          </p>
        </div>
        <button
          onClick={dismissBlockaidBanner}
          aria-label="Dismiss"
          className="text-ink-600 hover:text-ink text-lg leading-none"
        >
          ×
        </button>
      </div>
    ) : null;

  // Shown when wallet is connected but on the wrong chain.
  const WrongChainBanner = () =>
    flow.wrongChain ? (
      <div className="mb-4 p-4 rounded-2xl bg-warn/5 border border-warn/30">
        <div className="font-display font-semibold text-warn mb-1">
          Wrong network
        </div>
        <p className="text-sm text-ink-700 mb-3">
          Your wallet is on a different network. AIML is on{' '}
          <strong>Polygon Mainnet</strong>.
        </p>
        <Button
          size="md"
          onClick={() => flow.switchToPolygon()}
          isLoading={flow.step === 'switching-chain'}
        >
          Switch to Polygon
        </Button>
      </div>
    ) : null;

  // Shown when a wallet step has been hanging > 30s.
  const StuckRecovery = () =>
    flow.stuck && (flow.step === 'approving' || flow.step === 'buying') ? (
      <div className="mt-4 p-4 rounded-2xl bg-warn/5 border border-warn/30">
        <div className="font-display font-semibold text-warn mb-1">
          Wallet not responding?
        </div>
        <p className="text-sm text-ink-700 mb-3">
          {flow.isMobile
            ? 'Open your wallet app manually and look for a pending request — sometimes mobile browsers don\'t bring it to the foreground automatically.'
            : 'Check your wallet pop-up or browser extension for a pending request.'}
        </p>
        <div className="flex flex-wrap gap-2">
          {flow.txHash && (
            <a
              href={`https://polygonscan.com/tx/${flow.txHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm px-4 py-2 rounded-full bg-paper-300 hover:bg-paper-400 text-ink font-semibold"
            >
              View tx on Polygonscan
            </a>
          )}
          <button
            onClick={flow.reset}
            className="text-sm px-4 py-2 rounded-full bg-danger/10 hover:bg-danger/20 text-danger font-semibold"
          >
            Cancel and reset
          </button>
        </div>
      </div>
    ) : null;

  // Shown after a successful buy.
  const SuccessCard = () =>
    flow.step === 'done' && flow.txHash ? (
      <div className="mt-4 p-5 rounded-2xl bg-success/5 border border-success/20">
        <div className="font-display text-xl font-bold text-success mb-1">
          ✓ Purchase successful
        </div>
        <p className="text-sm text-ink-700 mb-3">
          Your AIML tokens are on the way to your wallet. To see them in MetaMask, add the
          AIML token using the contract address below.
        </p>

        <div className="bg-paper-100 p-3 rounded-xl mb-3">
          <div className="text-xs text-ink-600 uppercase tracking-wide mb-1">
            AIML Token Address
          </div>
          <div className="font-mono text-xs break-all">{AIML_TOKEN_ADDRESS}</div>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(AIML_TOKEN_ADDRESS).then(() =>
                toast.success('Copied'),
              );
            }}
            className="mt-2 text-xs text-violet-600 underline"
          >
            Copy address
          </button>
        </div>

        <div className="text-xs font-mono text-ink-600 mb-2 break-all">
          Tx: {flow.txHash}
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <a
            href={`https://polygonscan.com/tx/${flow.txHash}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-violet-600 underline font-semibold"
          >
            View on Polygonscan →
          </a>
          {receipt.data && (
            <span className="text-xs text-success">
              · Confirmed in block {Number(receipt.data.blockNumber)}
            </span>
          )}
          <button
            onClick={flow.reset}
            className="ml-auto text-xs text-ink-600 underline"
          >
            Buy again
          </button>
        </div>
      </div>
    ) : null;

  // ---------------------------------------------------------------------

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center mb-12">
        <div className="eyebrow mb-3">PRESALE BUY</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
          Buy <span className="text-gradient">$AIML</span> with USDT
        </h1>
        <p className="mt-3 text-ink-600">
          Polygon mainnet. Connect your wallet, approve once, buy in seconds.
        </p>
      </div>

      <BlockaidNotice />

      {presale.paused && (
        <div className="max-w-2xl mx-auto mb-8 p-4 rounded-2xl bg-warn/5 border border-warn/30 text-center">
          <div className="font-display font-semibold text-warn">Presale paused</div>
          <p className="text-sm text-ink-700 mt-1">
            Token sales are currently paused. Check back soon.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard
          label="RAISED"
          value={presale.totalUsdtRaised !== null ? fmtUsdt(presale.totalUsdtRaised) : '—'}
          accent="violet"
        />
        <StatCard
          label="RATE"
          value={presale.tokensPerUsdt !== null ? fmtAiml(presale.tokensPerUsdt) : '—'}
          hint="AIML / USDT"
          accent="magenta"
        />
        <StatCard
          label="SOLD"
          value={presale.totalAimlSold !== null ? fmtAiml(presale.totalAimlSold) : '—'}
          hint="AIML"
          accent="azure"
        />
      </div>

      {presale.error && (
        <div className="mb-6 p-4 rounded-2xl bg-danger/5 border border-danger/30">
          <div className="font-display font-semibold text-danger mb-1">
            Couldn't read contract data
          </div>
          <div className="text-xs font-mono text-ink-700 break-all">
            {(presale.error as Error).message || String(presale.error)}
          </div>
        </div>
      )}

      <Card className="!p-8">
        {referralCode && (
          <div className="mb-6 p-3 rounded-2xl bg-violet-500/5 border border-violet-500/20 flex items-center justify-between">
            <div className="text-sm">
              <span className="font-mono text-violet-600 font-semibold">{referralCode}</span>
              <span className="text-ink-600 ml-2">applied as your referrer</span>
            </div>
            <Badge kind="info">REF</Badge>
          </div>
        )}

        <WrongChainBanner />

        <FormField
          label="Amount in USDT"
          hint={
            quote.isLoading
              ? 'Fetching quote from contract…'
              : tokensOut > 0
                ? `You'll receive ≈ ${fmtAiml(tokensOut)} AIML`
                : 'Enter any positive amount in USDT'
          }
        >
          <div className="relative">
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              value={usdt}
              onChange={(e) => setUsdt(e.target.value)}
              placeholder="100"
              className="!h-16 !text-2xl !pr-24 font-display"
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2 font-mono font-semibold text-ink-600">
              USDT
            </div>
          </div>
        </FormField>

        <div className="mt-6 p-5 rounded-2xl bg-paper-200 flex items-center justify-between">
          <div>
            <div className="eyebrow mb-1">YOU RECEIVE</div>
            <div className="font-display text-3xl font-bold text-gradient tabular-nums">
              {fmtAiml(tokensOut)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-ink-600 font-mono">AIML</div>
            <div className="text-xs text-ink-600 mt-1">live quote</div>
          </div>
        </div>

        {/* Affiliate tier nudge */}
        <div className="mt-4 p-3 rounded-2xl bg-violet-500/5 border border-violet-500/10 text-xs text-ink-700">
          💡 Buy $150+ to become a <strong>Bronze Affiliate</strong> · $300+ to upgrade to{' '}
          <strong>Silver</strong>. Unlocks your referral link and 15–20% commission on every
          friend you bring.
        </div>

        {/* Mobile hint while a wallet step is active */}
        {flow.isMobile && isWorking && (
          <div className="mt-4 p-3 rounded-2xl bg-azure-500/5 border border-azure-500/20 text-xs text-ink-700">
            📱 <strong>Stay on this page.</strong> Open your wallet app to confirm the
            request, then come back — don't close this tab.
          </div>
        )}

        <div className="mt-6">
          {!isConnected ? (
            <ConnectButton.Custom>
              {({ openConnectModal }) => (
                <Button size="lg" className="w-full" onClick={openConnectModal}>
                  Connect wallet to buy
                </Button>
              )}
            </ConnectButton.Custom>
          ) : flow.wrongChain ? (
            <Button
              size="lg"
              className="w-full"
              onClick={() => flow.switchToPolygon()}
              isLoading={flow.step === 'switching-chain'}
            >
              Switch to Polygon
            </Button>
          ) : (
            <Button
              size="lg"
              className="w-full"
              onClick={handleBuy}
              isLoading={isWorking}
              disabled={!canBuy || isWorking || presale.paused}
            >
              {flowLabel(flow.step) ||
                (canBuy
                  ? `Buy ${fmtAiml(tokensOut)} AIML for ${fmtUsdt(usdtNum)}`
                  : 'Enter an amount')}
            </Button>
          )}
        </div>

        {/* Stuck-flow recovery panel */}
        <StuckRecovery />

        {flow.errorMessage && (
          <div className="mt-4 p-3 rounded-2xl bg-danger/5 border border-danger/20 text-danger text-sm flex items-start justify-between gap-3">
            <span className="flex-1">{flow.errorMessage}</span>
            <button
              onClick={flow.reset}
              className="text-danger underline shrink-0"
            >
              Reset
            </button>
          </div>
        )}

        {/* If we have a tx hash but the flow isn't 'done' yet, surface a passive link */}
        {flow.txHash && flow.step !== 'done' && (
          <div className="mt-4 p-3 rounded-2xl bg-paper-200 text-xs">
            <span className="text-ink-600">Tx submitted: </span>
            <a
              href={`https://polygonscan.com/tx/${flow.txHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-violet-600 underline break-all"
            >
              {flow.txHash.slice(0, 18)}…{flow.txHash.slice(-6)}
            </a>
          </div>
        )}

        <SuccessCard />
      </Card>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div className="card-base p-5">
          <div className="font-display font-semibold text-ink mb-1">USDT goes direct</div>
          <p className="text-ink-600 text-xs">
            Your USDT transfers to the project's ops wallet on Polygon — verifiable on
            Polygonscan.
          </p>
        </div>
        <div className="card-base p-5">
          <div className="font-display font-semibold text-ink mb-1">Affiliate program</div>
          <p className="text-ink-600 text-xs">
            Buy $150+ to unlock your referral link. Earn 15-22% USDT cash + AIML bonus on every
            friend's purchase.
          </p>
        </div>
        <div className="card-base p-5">
          <div className="font-display font-semibold text-ink mb-1">Vesting starts at TGE</div>
          <p className="text-ink-600 text-xs">
            AIML tokens unlock linearly over six months from token generation event, including
            bonuses.
          </p>
        </div>
      </div>
    </div>
  );
}

function flowLabel(step: string): string | null {
  switch (step) {
    case 'switching-chain':
      return 'Switching to Polygon…';
    case 'approving':
      return 'Approve USDT in your wallet…';
    case 'approve-confirming':
      return 'Waiting for approval confirmation…';
    case 'buying':
      return 'Confirm purchase in your wallet…';
    case 'buy-confirming':
      return 'Waiting for buy confirmation…';
    case 'attributing':
      return 'Recording attribution…';
    default:
      return null;
  }
}
